import {
  CAMPAIGN_COPY_FIELDS,
  campaignCopyField,
  type CampaignCopyFieldId,
} from '../../lib/campaignCopy/fields.js';
import type {
  CampaignCopyBudgetReport,
  CampaignCopyOption,
} from '../../lib/campaignCopy/requests.js';

// FRANCHISE CAMPAIGN COPY — the workspace's pure view logic. Kept out of the
// component so the sentences an AM reads about their own copy are testable
// without mounting a modal.

/** The order fields are offered and rendered in. Meta first, as the form has it. */
export const COPY_FIELD_ORDER: readonly CampaignCopyFieldId[] = CAMPAIGN_COPY_FIELDS.map(
  (f) => f.id,
);

export function groupOptionsByField(
  options: CampaignCopyOption[],
): Array<{ field: CampaignCopyFieldId; options: CampaignCopyOption[] }> {
  return COPY_FIELD_ORDER.map((field) => ({
    field,
    options: options.filter((o) => o.field === field),
  })).filter((group) => group.options.length > 0);
}

/**
 * ⚠️⚠️ THE LENGTH LINE — what the AM reads under each option, and the whole
 * reason the worst-case number is computed at all.
 *
 * An authored count alone is the number that MISLEADS: "29 characters" on a
 * headline containing {shortSuburb} looks safely under Google's 30 and is not.
 * So where a field has a platform limit, the RESOLVED figure leads and the
 * authored one is the aside, with the brand's longest suburb named so the number
 * is checkable rather than magic.
 */
export function describeOptionLength(
  option: CampaignCopyOption,
  budget: CampaignCopyBudgetReport,
): string {
  const field = campaignCopyField(option.field);
  if (field.platformMaxChars === null || option.resolvedChars === null) {
    return `${option.chars} characters`;
  }
  const suburb =
    budget.longestShortSuburb && option.resolvedChars !== option.chars
      ? ` at "${budget.longestShortSuburb.text}"`
      : '';
  return `${option.resolvedChars} of ${field.platformMaxChars} characters${suburb}`;
}

/** True when the option only fits because the brand's suburbs happen to be short. */
export function optionIsTight(
  option: CampaignCopyOption,
  thresholdChars = 2,
): boolean {
  const field = campaignCopyField(option.field);
  if (field.platformMaxChars === null || option.resolvedChars === null) return false;
  return field.platformMaxChars - option.resolvedChars <= thresholdChars;
}

/**
 * ⚠️ SAID OUT LOUD, on screen. "Validated against your longest suburb" and
 * "validated against a conservative fallback" are different promises, and only
 * one of them is a guarantee. A brand with no locations recorded yet gets copy
 * checked against a deliberately generous estimate — useful, but not the same
 * thing, and the AM should know which they are looking at.
 */
export function describeBudget(budget: CampaignCopyBudgetReport): string {
  if (!budget.fromLocations || !budget.longestShortSuburb) {
    return 'This brand has no location details recorded yet, so Google lengths were checked against a conservative estimate rather than your real suburbs.';
  }
  return `Google lengths were checked at this brand's longest suburb — "${budget.longestShortSuburb.text}" (${budget.longestShortSuburb.chars} characters) — across ${budget.locationCount} location${budget.locationCount === 1 ? '' : 's'}.`;
}

/**
 * What to say while the AM waits.
 *
 * ⚠️⚠️ THE HONEST VERSION, and it differs from the caption drafter's on purpose.
 * A full campaign run — five fields, fifteen Google headlines — is Opus writing
 * a couple of thousand tokens on top of reference and Drive reads, so it can run
 * to about a minute. Telling someone "about 25 seconds" and then taking 60 is
 * how they conclude it has hung and click again, which pays for a second Opus
 * call. It states a real expectation, and past that says it is running long
 * rather than implying it is nearly done.
 *
 * ⚠️ THE PHASE IS REQUIRED, not defaulted. A generation with no offer block yet
 * READS THE DOCUMENT FIRST — a Drive fetch plus a 58k-token model call, which is
 * tens of seconds during which nothing is being written. Describing that as
 * "writing" is the small lie that makes the whole label untrustworthy, and the
 * first thing an AM does with an untrustworthy progress line is click again.
 * Requiring it means a future caller has to say which half it is describing.
 */
export type CampaignCopyWaitPhase = 'reading' | 'writing';

export const CAMPAIGN_COPY_EXPECTED_SECONDS = 45;

/**
 * ⚠️ Lower than the writing expectation on purpose: the read is Sonnet
 * extracting from a document, not Opus composing five fields.
 */
export const CAMPAIGN_OFFER_READ_EXPECTED_SECONDS = 30;

export function copyWaitLabel(seconds: number, phase: CampaignCopyWaitPhase): string {
  if (phase === 'reading') {
    // ⚠️ "Once" is the load-bearing word. This wait happens on the FIRST
    // generation and never again for this campaign, and an AM who does not know
    // that reads a 30-second pause as what every generation will cost.
    if (seconds < CAMPAIGN_OFFER_READ_EXPECTED_SECONDS) {
      return `Reading your offer document — this happens once (${seconds}s)`;
    }
    return `Still reading the offer document (${seconds}s) — longer than usual, but it is still going`;
  }
  if (seconds < 4) return 'Reading your reference links…';
  if (seconds < CAMPAIGN_COPY_EXPECTED_SECONDS) {
    return `Writing — up to about a minute (${seconds}s)`;
  }
  return `Still writing (${seconds}s) — longer than usual, but it is still going`;
}

/**
 * The confirmation an AM sees before an insert replaces something.
 *
 * ⚠️ It names the copy that would GO, not just the fact that something would.
 * "This will replace an existing entry" is a warning nobody can act on; the
 * actual text is what lets them decide.
 */
export function describeReplacement(existing: string, max = 80): string {
  const trimmed = existing.trim();
  const shown = trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
  return `Replace "${shown}"`;
}
